import { BookingRemarksComponent } from '@components/shared/booking-remarks/booking-remarks.component';
import { ErrorStateComponent } from '@components/shared/error-state/error-state.component';
import { ChainMarksComponent } from '@components/shared/chain-marks/chain-marks.component';
import { FlatInfoComponent } from '@components/shared/flat-info/flat-info.component';
import { GuestInfoComponent } from '@components/shared/guest-info/guest-info.component';
import { FlatInfo } from '@models/flat-info.model';
import { TranslateModule } from '@ngx-translate/core';
import { format, parseISO } from 'date-fns';
import { ButtonModule } from 'primeng/button';
import { TableModule } from 'primeng/table';
import { TooltipModule } from 'primeng/tooltip';
import { CommonModule } from '@angular/common';
import { Component, EventEmitter, inject, input, Input, output, Output } from '@angular/core';
import { GuestInfoDisplayMode } from '@models/guest-info.model';
import { GuestBookingRow, PresenceStatus } from '../../models/booking-overview.model';
import { createSkeletonRows, ListLoadStatus, SkeletonColumn, SkeletonRowPlaceholder, TableView, tableViewFor } from '../../models/list-load-state';
import { OverviewSkeletonRowComponent } from '../skeleton-row/overview-skeleton-row.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-guests-table',
  standalone: true,
  imports: [CommonModule, TranslateModule, TableModule, ButtonModule, TooltipModule, FlatInfoComponent, GuestInfoComponent, ChainMarksComponent, BookingRemarksComponent, ErrorStateComponent, OverviewSkeletonRowComponent],
  template: `
    <div class="booking-table-container">
      <!-- Load error -->
      @if (view === 'error') {
        <app-error-state
          [title]="'bookingOverview.loadError.title'"
          [message]="'bookingOverview.loadError.message'"
          [retryText]="'common.retry'"
          (onRetry)="retry.emit()"
        />
      }

      <!-- Empty state message -->
      @if (view === 'empty') {
        <div class="p-4 text-center">
          @if (isFilterActive && totalCount > 0) {
            <div>
              <i class="pi pi-filter mb-2 text-xl text-primary"></i>
              <p>{{ 'bookingOverview.noGuestsFiltered' | translate }}</p>
              <p class="text-500 mt-2 text-sm">{{ 'bookingOverview.adjustFilters' | translate }}</p>
            </div>
          } @else {
            <p>{{ 'bookingOverview.noGuests' | translate }}</p>
          }
        </div>
      }

      <!-- Table -->
      @if (view === 'rows' || view === 'skeleton') {
        <p-table
          [value]="tableRows"
          styleClass="p-datatable-sm"
          [tableStyle]="{ 'min-width': '50rem' }"
          [showCurrentPageReport]="true"
          dataKey="@id"
          [sortField]="sortField"
          [sortOrder]="sortOrder"
          (onSort)="onSort($event)"
        >
          <ng-template pTemplate="header">
            <tr>
              <th
                [pSortableColumn]="'guest.name'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.guest' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'guest.name' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'guest.name' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'guest.name') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'flatInfo'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.flat' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'flatInfo' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'flatInfo' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'flatInfo') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th style="vertical-align: middle; width: 80px;" class="text-center">
                {{ 'bookingOverview.presence' | translate }}
              </th>
              <th style="vertical-align: middle">{{ 'bookingOverview.entryCode' | translate }}</th>
              <th
                [pSortableColumn]="'checkInDate'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkIn' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'checkInDate' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'checkInDate' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'checkInDate') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'checkOutDate'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkOut' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'checkOutDate' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'checkOutDate' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'checkOutDate') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th
                [pSortableColumn]="'isCheckedIn'"
                style="vertical-align: middle"
              >
                <div class="flex justify-between">
                  {{ 'bookingOverview.checkedIn' | translate }}
                  <span class="ml-1">
                    @if (sortState.field === 'isCheckedIn' && sortState.direction === 'asc') {
                      <i class="pi pi-sort-up"></i>
                    }
                    @if (sortState.field === 'isCheckedIn' && sortState.direction === 'desc') {
                      <i class="pi pi-sort-down"></i>
                    }
                    @if (sortState.field !== 'isCheckedIn') {
                      <i class="pi pi-sort text-300"></i>
                    }
                  </span>
                </div>
              </th>
              <th style="vertical-align: middle">{{ 'common.actions' | translate }}</th>
            </tr>
          </ng-template>
          <ng-template
            pTemplate="body"
            let-booking
          >
            @if (view === 'skeleton') {
              <tr appOverviewSkeletonRow [columns]="skeletonColumns"></tr>
            } @else {
              <tr class="animate-fadein animate-duration-200 animate-ease-out">
                <td>
                  <app-guest-info [guestInfo]="booking.guest" [displayMode]="displayMode">
                    <app-booking-remarks guestNameSuffix [remarks]="booking.remarks" />
                  </app-guest-info>
                  <div class="mt-1 flex flex-wrap gap-1"><app-chain-marks [marks]="booking.chain"></app-chain-marks></div>
                </td>
                <td>
                  <ng-container *ngFor="let flatInfo of booking.flatInfos; let i = index">
                    <app-flat-info [flatInfo]="flatInfo"></app-flat-info>
                    <span *ngIf="i < booking.flatInfos.length - 1"><br /></span>
                  </ng-container>
                </td>
                <td class="text-center">
                  @if (booking.presenceLoading) {
                    <i class="pi pi-spin pi-spinner text-sm"></i>
                  }
                  @if (!booking.presenceLoading && booking.presenceStatus && booking.presenceStatus.isOccupied) {
                    <div 
                      class="cursor-pointer inline-flex items-center justify-center"
                      (click)="onPresenceClick(booking)"
                      [pTooltip]="getPresenceTooltip(booking)"
                      tooltipPosition="top">
                      <div class="flex h-6 w-6 items-center justify-center rounded-full bg-green-100">
                        <i class="pi pi-check text-sm text-green-600"></i>
                      </div>
                    </div>
                  }
                  @if (!booking.presenceLoading && booking.presenceStatus && !booking.presenceStatus.isOccupied) {
                    <div 
                      class="cursor-pointer inline-flex items-center justify-center"
                      (click)="onPresenceClick(booking)"
                      [pTooltip]="getPresenceTooltip(booking)"
                      tooltipPosition="top">
                      <div class="flex h-6 w-6 items-center justify-center rounded-full bg-gray-100">
                        <i class="pi pi-minus text-sm text-gray-400"></i>
                      </div>
                    </div>
                  }
                  @if (!booking.presenceLoading && !booking.presenceStatus) {
                    <span class="text-gray-400  text-sm">—</span>
                  }
                </td>
                <td>
                  <!-- Loading state -->
                  @if (booking.entryCodesLoading) {
                    <div class="flex items-center justify-center">
                      <i
                        class="pi pi-spin pi-spinner"
                        style="font-size: 0.75rem"
                      ></i>
                    </div>
                  }
                  <!-- Entry codes loaded -->
                  @if (!booking.entryCodesLoading && booking.entryCodes.length > 0) {
                    <div class="text-center">
                      @for (code of booking.entryCodes; track $index) {
                        <div>
                          <span class="font-bold">{{ code }}</span>
                          @if (booking.flatInfos.length > 1 && $index < booking.entryCodes.length - 1) {
                            <br />
                          }
                        </div>
                      }
                      @if (booking.entryCodesFailed) {
                        <i
                          class="pi pi-exclamation-triangle text-sm text-orange-500 dark:text-orange-400"
                          role="img"
                          [attr.aria-label]="'bookingOverview.entryCodeLoadFailed' | translate"
                          [pTooltip]="'bookingOverview.entryCodeLoadFailed' | translate"
                          tooltipPosition="top"
                        ></i>
                      }
                    </div>
                  }
                  <!-- No entry codes available -->
                  @if (!booking.entryCodesLoading && booking.entryCodes.length === 0) {
                    <div class="text-center">
                      @if (booking.entryCodesFailed) {
                        <i
                          class="pi pi-exclamation-triangle text-sm text-orange-500 dark:text-orange-400"
                          role="img"
                          [attr.aria-label]="'bookingOverview.entryCodeLoadFailed' | translate"
                          [pTooltip]="'bookingOverview.entryCodeLoadFailed' | translate"
                          tooltipPosition="top"
                        ></i>
                      } @else {
                        <span class="text-sm text-gray-400">{{ booking.entryCode }}</span>
                      }
                    </div>
                  }
                </td>
                <td>{{ formatDate(booking.checkInDate) }}</td>
                <td>{{ formatDate(booking.checkOutDate) }}</td>
                <td class="text-center">
                  <div class="flex justify-center">
                    @if (booking.isCheckedIn) {
                      <div class="flex items-center justify-center rounded-full bg-green-100 p-1 text-green-700">
                        <i class="pi pi-check !text-sm font-bold !leading-none"></i>
                      </div>
                    } @else {
                      <span class="text-lg text-red-300">—</span>
                    }
                  </div>
                </td>
                <td>
                  <div class="flex gap-2">
                    <button
                      pButton
                      icon="pi pi-eye"
                      class="p-button-outlined p-button-sm rounded-md"
                      pTooltip="{{ 'common.viewDetails' | translate }}"
                      tooltipPosition="top"
                      (click)="onGuestInfoClick(booking)"
                    ></button>
                    <button
                      pButton
                      icon="pi pi-comments"
                      class="p-button-outlined p-button-sm rounded-md"
                      [disabled]="!!booking.entryCodesFailed"
                      pTooltip="{{ (booking.entryCodesFailed ? 'bookingOverview.communicationUnavailableNoEntryCode' : 'communication.title') | translate }}"
                      tooltipPosition="top"
                      (click)="onCommunicationClick(booking)"
                    ></button>
                    <button
                      pButton
                      icon="pi pi-envelope"
                      class="p-button-outlined p-button-sm rounded-md"
                      [disabled]="!!booking.entryCodesFailed"
                      pTooltip="{{ (booking.entryCodesFailed ? 'bookingOverview.entryCodeSendUnavailable' : 'bookingOverview.sendCodeEmail') | translate }}"
                      tooltipPosition="top"
                      (click)="onSendEmailClick(booking)"
                    ></button>
                    <button
                      pButton
                      icon="pi pi-mobile"
                      class="p-button-outlined p-button-sm rounded-md"
                      [disabled]="!!booking.entryCodesFailed"
                      pTooltip="{{ (booking.entryCodesFailed ? 'bookingOverview.entryCodeSendUnavailable' : 'bookingOverview.sendCodeSms') | translate }}"
                      tooltipPosition="top"
                      (click)="onSendSmsClick(booking)"
                    ></button>
                    <button
                      pButton
                      icon="pi pi-file"
                      class="p-button-outlined p-button-sm rounded-md"
                      pTooltip="{{ 'bookingOverview.invoice' | translate }}"
                      tooltipPosition="top"
                      (click)="onInvoiceClick(booking)"
                    ></button>
                    @if (booking.isCheckedIn) {
                      <button
                        pButton
                        icon="pi pi-copy"
                        class="p-button-outlined p-button-sm p-button-success rounded-md"
                        pTooltip="{{ 'bookingOverview.copyCheckin' | translate }}"
                        tooltipPosition="top"
                        (click)="onCopyCheckinClick(booking)"
                      ></button>
                    }
                    @if (!booking.isCheckedIn && hasCheckinInClipboard) {
                      <button
                        pButton
                        icon="pi pi-clipboard"
                        class="p-button-outlined p-button-sm p-button-warning rounded-md"
                        pTooltip="{{ 'bookingOverview.pasteCheckin' | translate }}"
                        tooltipPosition="top"
                        (click)="onPasteCheckinClick(booking)"
                      ></button>
                    }
                  </div>
                </td>
              </tr>
            }
          </ng-template>
        </p-table>
      }
    </div>
  `
})
export class GuestsTableComponent {
  @Input() guests: GuestBookingRow[] = [];
  @Input() isFilterActive: boolean = false;
  @Input() totalCount: number = 0;
  @Input() displayMode: GuestInfoDisplayMode = GuestInfoDisplayMode.USER;
  @Input() hasCheckinInClipboard: boolean = false;

  readonly loadStatus = input<ListLoadStatus>('ready');
  readonly retry = output<void>();

  protected readonly skeletonRows = createSkeletonRows();
  protected readonly skeletonColumns: readonly SkeletonColumn[] = ['guest', 'flat', 'icon', 'text', 'text', 'text', 'icon', 'actions'];

  /** Töltés közben helykitöltő, hibánál hibaállapot, különben a sorok vagy az üres állapot. */
  protected get view(): TableView {
    return tableViewFor(this.loadStatus(), this.guests?.length ?? 0);
  }

  /** A p-table sorai: töltés közben a helykitöltők, különben a kapott sorok. */
  protected get tableRows(): (GuestBookingRow | SkeletonRowPlaceholder)[] {
    return this.view === 'skeleton' ? this.skeletonRows : this.guests;
  }

  sortField: string = '';
  sortOrder: number = 1;

  @Output() guestInfoClicked = new EventEmitter<number>();
  @Output() sendEmail = new EventEmitter<number>();
  @Output() sendSms = new EventEmitter<number>();
  @Output() generateInvoice = new EventEmitter<number>();
  @Output() communicationClicked = new EventEmitter<GuestBookingRow>();
  @Output() presenceClicked = new EventEmitter<{ booking: GuestBookingRow; presenceStatus: PresenceStatus | undefined }>();
  @Output() copyCheckin = new EventEmitter<GuestBookingRow>();
  @Output() pasteCheckin = new EventEmitter<GuestBookingRow>();

  private readonly translateService = inject(TranslateService);

  // Sort state for UI display
  sortState: { field: string | null; direction: 'asc' | 'desc' | null } = {
    field: null,
    direction: null
  };

  constructor() {}

  /**
   * Format date to display in table
   * @param date Date string to format
   * @returns Formatted date string
   */
  formatDate(date: string): string {
    if (!date) return '';
    try {
      return format(parseISO(date), 'yyyy-MM-dd');
    } catch (e) {
      return '';
    }
  }

  /**
   * Handle sort event from p-table
   * @param event Sort event data
   */
  onSort(event: any): void {
    this.sortField = event.field;
    this.sortOrder = event.order;

    // Update sort state for custom icons
    this.sortState.field = event.field;
    this.sortState.direction = event.order === 1 ? 'asc' : 'desc';

    if (this.sortField === 'flatInfo') {
      this.guests = [...this.guests].sort((a, b) => {
        const result = FlatInfo.compare(a.flatInfos[0], b.flatInfos[0]);
        return this.sortOrder === 1 ? result : -result;
      });
    }
  }

  /**
   * Handle guest info button click
   * @param booking Booking data
   */
  onGuestInfoClick(booking: GuestBookingRow): void {
    if (booking.id) {
      this.guestInfoClicked.emit(booking.id);
    }
  }

  /**
   * Handle communication button click
   * @param booking Booking data
   */
  onCommunicationClick(booking: GuestBookingRow): void {
    if (booking.entryCodesFailed) {
      return;
    }
    this.communicationClicked.emit(booking);
  }

  /**
   * Handle presence button click
   * @param booking Booking data
   */
  onPresenceClick(booking: GuestBookingRow): void {
    this.presenceClicked.emit({
      booking,
      presenceStatus: booking.presenceStatus
    });
  }

  /**
   * Get presence tooltip
   * @param booking Booking data
   * @returns Presence tooltip
   */
  getPresenceTooltip(booking: GuestBookingRow): string {
    if (!booking.presenceStatus) return '';
    
    const status = booking.presenceStatus.isOccupied 
      ? this.translateService.instant('presence.occupied')
      : this.translateService.instant('presence.empty');
    
    return `${status} • ${this.getTimeSince(booking.presenceStatus.minutesSinceChange)}`;
  }

  /**
   * Get time since presence status change
   * @param minutes Minutes since presence status change
   * @returns Time since presence status change
   */
  private getTimeSince(minutes: number): string {
    if (minutes < 1) return 'kevesebb mint 1 perce';
    if (minutes < 60) return `${Math.floor(minutes)} perce`;
    const hours = Math.floor(minutes / 60);
    if (hours === 1) return '1 órája';
    if (hours < 24) return `${hours} órája`;
    const days = Math.floor(hours / 24);
    return `${days} napja`;
  }

  /**
   * Handle send email button click
   * @param booking Booking data
   */
  onSendEmailClick(booking: GuestBookingRow): void {
    if (booking.id && !booking.entryCodesFailed) {
      this.sendEmail.emit(booking.id);
    }
  }

  /**
   * Handle send SMS button click
   * @param booking Booking data
   */
  onSendSmsClick(booking: GuestBookingRow): void {
    if (booking.id && !booking.entryCodesFailed) {
      this.sendSms.emit(booking.id);
    }
  }

  /**
   * Handle invoice button click
   * @param booking Booking data
   */
  onInvoiceClick(booking: GuestBookingRow): void {
    if (booking.id) {
      this.generateInvoice.emit(booking.id);
    }
  }

  /**
   * Handle copy checkin button click
   * @param booking Booking data
   */
  onCopyCheckinClick(booking: GuestBookingRow): void {
    this.copyCheckin.emit(booking);
  }

  /**
   * Handle paste checkin button click
   * @param booking Booking data
   */
  onPasteCheckinClick(booking: GuestBookingRow): void {
    this.pasteCheckin.emit(booking);
  }
}
